export enum ShigoRole {
  SuperAdmin = 'SUPER_ADMIN',
  Admin = 'ADMIN',
  AdminReadOnly = 'ADMIN_READ_ONLY',
  User = 'USER',
  UserReadOnly = 'USER_READ_ONLY',
  Publisher = 'PUBLISHER',
  Agency = 'AGENCY',
  Advertiser = 'ADVERTISER',
  AdvertiserReadOnly = 'ADVERTISER_READ_ONLY',
  Mcp = 'MCP',
  SalesRepresentative = 'SALES_REPRESENTATIVE',
}

export const assignableShigoRoles = [
  ShigoRole.SuperAdmin,
  ShigoRole.Admin,
  ShigoRole.AdminReadOnly,
  ShigoRole.Publisher,
  ShigoRole.Agency,
  ShigoRole.Advertiser,
  ShigoRole.AdvertiserReadOnly,
  ShigoRole.User,
  ShigoRole.UserReadOnly,
  ShigoRole.Mcp,
] as const;

export type AssignableShigoRole = (typeof assignableShigoRoles)[number];

const assignableShigoRoleSet = new Set<string>(assignableShigoRoles);

export function isAssignableShigoRole(value: string): value is AssignableShigoRole {
  return assignableShigoRoleSet.has(value);
}

export enum ShigoPermission {
  All = '*:*',
  HomeRead = 'home:read',
  SupplyRead = 'supply:read',
  SupplyWrite = 'supply:write',
  SupplyPublishersRead = 'supply.publishers:read',
  SupplyPublishersCreate = 'supply.publishers:create',
  SupplyPublishersEdit = 'supply.publishers:edit',
  SupplyPublishersOverviewRead = 'supply.publishers.overview:read',
  SupplyPublishersAppsRead = 'supply.publishers.apps:read',
  SupplyPublishersAppsCreate = 'supply.publishers.apps:create',
  SupplyPublishersAppsUpdate = 'supply.publishers.apps:update',
  SupplyPublishersAppsArchive = 'supply.publishers.apps:archived',
  SupplyPublishersDomainsRead = 'supply.publishers.domains:read',
  SupplyPublishersDomainsCreate = 'supply.publishers.domains:create',
  SupplyPublishersDomainsUpdate = 'supply.publishers.domains:update',
  SupplyPublishersDomainsExport = 'supply.publishers.domains:export',
  SupplyPublishersPlacementsRead = 'supply.publishers.placements:read',
  SupplyPublishersPlacementsCreate = 'supply.publishers.placements:create',
  SupplyPublishersPlacementsEdit = 'supply.publishers.placements:edit',
  SupplyPublishersPlacementsArchive = 'supply.publishers.placements:archive',
  SupplyPublishersAppRequestsRead = 'supply.publishers.app-requests:read',
  SupplyPublishersA2vRead = 'supply.publishers.a2v:read',
  SupplyPublishersA2vSave = 'supply.publishers.a2v:save',
  SupplyPublishersChangelogRead = 'supply.publishers.changelog:read',
  SupplyPublishersEndpointGeneratorGenerate = 'supply.publishers.endpoint-generator:generate',
  SupplyPlacementsRead = 'supply.placements:read',
  SupplyPlacementsCreate = 'supply.placements:create',
  SupplyPlacementsUpdate = 'supply.placements:update',
  SupplyPlacementsEdit = 'supply.placements:edit',
  SupplyPlacementsArchive = 'supply.placements:archive',
  SupplyAppsRead = 'supply.apps:read',
  SupplyAppsInventoryRead = 'supply.apps.apps:read',
  SupplyAppsInventoryEdit = 'supply.apps.apps:edit',
  SupplyAppsInventoryArchive = 'supply.apps.apps:archive',
  SupplyAppsInventoryImport = 'supply.apps.apps:import',
  SupplyAppsInventoryExport = 'supply.apps.apps:export',
  SupplyAppRequestsRead = 'supply.apps.app-requests:read',
  SupplyAppRequestsExport = 'supply.apps.app-requests:export',
  SupplyAppRequestEdit = 'APP_REQUEST_EDIT',
  SupplyAppRequestDisconnect = 'APP_REQUEST_DISCONNECT',
  SupplyAppRequestCreate = 'APP_REQUEST_CREATE',
  SupplyListsRead = 'supply.lists:read',
  SupplyListsCreate = 'supply.lists:create',
  SupplyListsEdit = 'supply.lists:edit',
  SupplyListsDelete = 'supply.lists:delete',
  SupplyListsImport = 'supply.lists:import',
  SupplyListsExport = 'supply.lists:export',
  SupplyEndpointGeneratorRead = 'supply.endpoint-generator:read',
  SupplyEndpointGeneratorGenerate = 'supply.endpoint-generator:generate',
  SupplyAdsTxtRead = 'supply.ads-txt:read',
  SupplyAdsTxtRecordsRead = 'supply.ads-txt.records:read',
  SupplyAdsTxtRecordsCreate = 'supply.ads-txt.records:create',
  SupplyAdsTxtRecordsUpdate = 'supply.ads-txt.records:update',
  SupplyAdsTxtRecordsDelete = 'supply.ads-txt.records:delete',
  SupplyAdsTxtRecordsExport = 'supply.ads-txt.records:export',
  SupplyAdsTxtRecordsEdit = 'supply.ads-txt.records:edit',
  SupplyAdsTxtRecordsCopy = 'supply.ads-txt.records:copy',
  SupplyAdsTxtAppsRead = 'supply.ads-txt.apps:read',
  SupplyAdsTxtAppsCrawl = 'supply.ads-txt.apps:crawl',
  SupplyAdsTxtDomainsRead = 'supply.ads-txt.domains:read',
  SupplyAdsTxtDomainsCrawl = 'supply.ads-txt.domains:crawl',
  DemandRead = 'demand:read',
  DemandPartnersRead = 'demand.partners:read',
  DemandPartnersCreate = 'demand.partners:create',
  DemandPartnersEdit = 'demand.partners:edit',
  DemandPartnersChangelogRead = 'demand.partners.changelog:read',
  DemandDealsRead = 'demand.deals:read',
  DemandDealsCreate = 'demand.deals:create',
  DemandDealsEdit = 'demand.deals:edit',
  DemandDealsArchive = 'demand.deals:archive',
  DemandIntegrationsRead = 'demand.connections:read',
  DemandIntegrationsCreate = 'demand.connections:create',
  DemandIntegrationsEdit = 'demand.connections:edit',
  DemandIntegrationsDuplicate = 'demand.connections:duplicate',
  DemandIntegrationsChangelogRead = 'demand.connections.changelog:read',
  DemandIntegrationPlacementsRead = 'demand.connctions.placements:read',
  DemandIntegrationPlacementsEdit = 'demand.connctions.placements:edit',
  DemandComplianceRead = 'demand.compliance:read',
  DemandComplianceDpsRead = 'demand.compliance.dps:read',
  DemandComplianceDpsEdit = 'demand.compliance.dps:edit',
  ComplianceDomainCreate = 'COMPLIANCE_DOMAIN_CREATE',
  DebugRead = 'debug:read',
  DebugCreate = 'debug:create',
  DebugEdit = 'debug:edit',
  ReportingRead = 'reporting:read',
  ReportingReportBuilderRead = 'reporting.report-builder:read',
  ReportingReportBuilderRun = 'reporting.report-builder:run',
  ReportingPerformanceRead = 'reporting.performance:read',
  ReportingCustomReportsCreate = 'CUSTOM_REPORTS_CREATE',
  ReportingSavedReportsRead = 'reporting.scheduled-reports:read',
  ReportingSavedReportsCreate = 'reporting.scheduled-reports:create',
  ReportingSavedReportsEdit = 'reporting.scheduled-reports:edit',
  ReportingSavedReportsDelete = 'reporting.scheduled-reports:delete',
  PublisherDashboardRead = 'publisher-dashboard:read',
  PublisherDashboardHomeRead = 'publisher-dashboard.home:read',
  PublisherDashboardReportingRead = 'publisher-dashboard.reporting:read',
  MadBuySupplyRead = 'madbuy.supply.read',
  MadBuySupplyWrite = 'madbuy.supply.write',
  MadBuyCampaignRead = 'madbuy.campaign.read',
  MadBuyCampaignWrite = 'madbuy.campaign.write',
  MadBuyCreativesRead = 'madbuy.creatives.read',
  MadBuyCreativesWrite = 'madbuy.creatives.write',
  TargetingRead = 'targeting:read',
  TargetingCreate = 'targeting:create',
  TargetingEdit = 'targeting:edit',
  TargetingArchive = 'targeting:archive',
  AdminUsersRead = 'admin.users:read',
  AdminUsersWrite = 'admin.users:write',
  AdminUsersEdit = 'admin.users:edit',
  AdminUsersDelete = 'admin.users:delete',
  AdminChangelogRead = 'admin.changelog:read',
  AdminChangelogExport = 'CHANGELOG_EXPORT',
  A2vRead = 'a2v:read',
  A2vAssetsRead = 'a2v.assets:read',
  A2vAssetsUpload = 'a2v.assets:upload',
  A2vAssetsEdit = 'a2v.assets:edit',
  A2vNetworkTemplateRead = 'a2v.network-template:read',
  A2vNetworkTemplateSave = 'a2v.network-template:save',
  BlocklistCreativesRead = 'blocklist.creatives:read',
  BlocklistCreativesWrite = 'blocklist.creatives:write',
  BlocklistCreativesEdit = 'blocklist.creatives:edit',
  BlocklistCreativesDelete = 'blocklist.creatives:delete',
  NetworkCreate = 'NETWORK_CREATE',
  NetworkEdit = 'NETWORK_EDIT',
  NetworkArchive = 'NETWORK_ARCHIVE',
}

export enum ShigoPermissionSource {
  Backend = 'BACKEND',
  LegacyRole = 'LEGACY_ROLE',
}

const readPermissions = [
  ShigoPermission.HomeRead,
  ShigoPermission.SupplyRead,
  ShigoPermission.SupplyPublishersRead,
  ShigoPermission.SupplyPublishersOverviewRead,
  ShigoPermission.SupplyPublishersAppsRead,
  ShigoPermission.SupplyPublishersDomainsRead,
  ShigoPermission.SupplyPublishersPlacementsRead,
  ShigoPermission.SupplyPublishersAppRequestsRead,
  ShigoPermission.SupplyPublishersA2vRead,
  ShigoPermission.SupplyPublishersChangelogRead,
  ShigoPermission.SupplyPlacementsRead,
  ShigoPermission.SupplyAppsRead,
  ShigoPermission.SupplyAppsInventoryRead,
  ShigoPermission.SupplyAppRequestsRead,
  ShigoPermission.SupplyListsRead,
  ShigoPermission.SupplyEndpointGeneratorRead,
  ShigoPermission.SupplyAdsTxtRead,
  ShigoPermission.SupplyAdsTxtRecordsRead,
  ShigoPermission.SupplyAdsTxtAppsRead,
  ShigoPermission.SupplyAdsTxtDomainsRead,
  ShigoPermission.DemandRead,
  ShigoPermission.DemandPartnersRead,
  ShigoPermission.DemandPartnersChangelogRead,
  ShigoPermission.DemandDealsRead,
  ShigoPermission.DemandIntegrationsRead,
  ShigoPermission.DemandIntegrationsChangelogRead,
  ShigoPermission.DemandIntegrationPlacementsRead,
  ShigoPermission.DemandComplianceRead,
  ShigoPermission.DemandComplianceDpsRead,
  ShigoPermission.DebugRead,
  ShigoPermission.ReportingRead,
  ShigoPermission.ReportingReportBuilderRead,
  ShigoPermission.ReportingReportBuilderRun,
  ShigoPermission.ReportingPerformanceRead,
  ShigoPermission.ReportingSavedReportsRead,
  ShigoPermission.PublisherDashboardRead,
  ShigoPermission.PublisherDashboardHomeRead,
  ShigoPermission.PublisherDashboardReportingRead,
  ShigoPermission.MadBuySupplyRead,
  ShigoPermission.MadBuyCampaignRead,
  ShigoPermission.MadBuyCreativesRead,
  ShigoPermission.TargetingRead,
  ShigoPermission.AdminUsersRead,
  ShigoPermission.AdminChangelogRead,
  ShigoPermission.A2vRead,
  ShigoPermission.A2vAssetsRead,
  ShigoPermission.A2vNetworkTemplateRead,
  ShigoPermission.BlocklistCreativesRead,
] as const;

const writePermissions = [
  ShigoPermission.SupplyWrite,
  ShigoPermission.SupplyPublishersCreate,
  ShigoPermission.SupplyPublishersEdit,
  ShigoPermission.SupplyPublishersAppsCreate,
  ShigoPermission.SupplyPublishersAppsUpdate,
  ShigoPermission.SupplyPublishersAppsArchive,
  ShigoPermission.SupplyPublishersDomainsCreate,
  ShigoPermission.SupplyPublishersDomainsUpdate,
  ShigoPermission.SupplyPublishersDomainsExport,
  ShigoPermission.SupplyPublishersPlacementsCreate,
  ShigoPermission.SupplyPublishersPlacementsEdit,
  ShigoPermission.SupplyPublishersPlacementsArchive,
  ShigoPermission.SupplyPublishersA2vSave,
  ShigoPermission.SupplyPublishersEndpointGeneratorGenerate,
  ShigoPermission.SupplyPlacementsCreate,
  ShigoPermission.SupplyPlacementsUpdate,
  ShigoPermission.SupplyPlacementsEdit,
  ShigoPermission.SupplyPlacementsArchive,
  ShigoPermission.SupplyAppsInventoryEdit,
  ShigoPermission.SupplyAppsInventoryArchive,
  ShigoPermission.SupplyAppsInventoryImport,
  ShigoPermission.SupplyAppsInventoryExport,
  ShigoPermission.SupplyAppRequestsExport,
  ShigoPermission.SupplyAppRequestEdit,
  ShigoPermission.SupplyAppRequestDisconnect,
  ShigoPermission.SupplyAppRequestCreate,
  ShigoPermission.SupplyListsCreate,
  ShigoPermission.SupplyListsEdit,
  ShigoPermission.SupplyListsDelete,
  ShigoPermission.SupplyListsImport,
  ShigoPermission.SupplyListsExport,
  ShigoPermission.DemandComplianceDpsEdit,
  ShigoPermission.ComplianceDomainCreate,
  ShigoPermission.SupplyEndpointGeneratorGenerate,
  ShigoPermission.SupplyAdsTxtRecordsCreate,
  ShigoPermission.SupplyAdsTxtRecordsUpdate,
  ShigoPermission.SupplyAdsTxtRecordsDelete,
  ShigoPermission.SupplyAdsTxtRecordsExport,
  ShigoPermission.SupplyAdsTxtRecordsEdit,
  ShigoPermission.SupplyAdsTxtRecordsCopy,
  ShigoPermission.SupplyAdsTxtAppsCrawl,
  ShigoPermission.SupplyAdsTxtDomainsCrawl,
  ShigoPermission.DemandIntegrationsCreate,
  ShigoPermission.DemandPartnersCreate,
  ShigoPermission.DemandPartnersEdit,
  ShigoPermission.DemandDealsCreate,
  ShigoPermission.DemandDealsEdit,
  ShigoPermission.DemandDealsArchive,
  ShigoPermission.DemandIntegrationsEdit,
  ShigoPermission.DemandIntegrationsDuplicate,
  ShigoPermission.DemandIntegrationPlacementsEdit,
  ShigoPermission.DebugCreate,
  ShigoPermission.DebugEdit,
  ShigoPermission.ReportingCustomReportsCreate,
  ShigoPermission.ReportingSavedReportsCreate,
  ShigoPermission.ReportingSavedReportsEdit,
  ShigoPermission.ReportingSavedReportsDelete,
  ShigoPermission.MadBuySupplyWrite,
  ShigoPermission.MadBuyCampaignWrite,
  ShigoPermission.MadBuyCreativesWrite,
  ShigoPermission.TargetingCreate,
  ShigoPermission.TargetingEdit,
  ShigoPermission.TargetingArchive,
  ShigoPermission.AdminUsersWrite,
  ShigoPermission.AdminUsersEdit,
  ShigoPermission.AdminUsersDelete,
  ShigoPermission.AdminChangelogExport,
  ShigoPermission.A2vAssetsUpload,
  ShigoPermission.A2vAssetsEdit,
  ShigoPermission.A2vNetworkTemplateSave,
  ShigoPermission.BlocklistCreativesWrite,
  ShigoPermission.BlocklistCreativesEdit,
  ShigoPermission.BlocklistCreativesDelete,
] as const;

const advertiserPermissions = [
  ShigoPermission.MadBuyCampaignRead,
  ShigoPermission.MadBuyCampaignWrite,
  ShigoPermission.MadBuyCreativesRead,
  ShigoPermission.MadBuyCreativesWrite,
  ShigoPermission.TargetingRead,
  ShigoPermission.TargetingCreate,
  ShigoPermission.TargetingEdit,
  ShigoPermission.TargetingArchive,
] as const;

const publisherPermissions = [
  ShigoPermission.PublisherDashboardRead,
  ShigoPermission.PublisherDashboardHomeRead,
  ShigoPermission.PublisherDashboardReportingRead,
] as const;

const advertiserReadPermissions = [
  ShigoPermission.MadBuyCampaignRead,
  ShigoPermission.MadBuyCreativesRead,
  ShigoPermission.TargetingRead,
] as const;

const knownShigoPermissions = new Set<string>(Object.values(ShigoPermission));

export function normalizeShigoPermissions(permissions: readonly string[]): ShigoPermission[] {
  return permissions.filter((permission): permission is ShigoPermission =>
    knownShigoPermissions.has(permission),
  );
}

export function hasShigoPermission(permissions: readonly string[], permission: ShigoPermission) {
  return permissions.includes(ShigoPermission.All) || permissions.includes(permission);
}

export function hasAnyShigoPermission(
  permissions: readonly string[],
  required: readonly ShigoPermission[],
) {
  return required.some((permission) => hasShigoPermission(permissions, permission));
}

export function hasAllShigoPermissions(
  permissions: readonly string[],
  required: readonly ShigoPermission[],
) {
  return required.every((permission) => hasShigoPermission(permissions, permission));
}

export function resolveLegacyRolePermissions(roles: readonly string[]): string[] {
  const roleSet = new Set(roles);
  const ownedRoles = new Set(Object.values(ShigoRole).filter((role) => roleSet.has(role)));
  if (ownedRoles.has(ShigoRole.SuperAdmin)) return [ShigoPermission.All];

  const resolved = new Set<ShigoPermission>();
  for (const role of ownedRoles) {
    if (role === ShigoRole.Admin || role === ShigoRole.User) {
      readPermissions.forEach((permission) => resolved.add(permission));
      writePermissions.forEach((permission) => resolved.add(permission));
    } else if (role === ShigoRole.AdminReadOnly || role === ShigoRole.UserReadOnly) {
      readPermissions.forEach((permission) => resolved.add(permission));
    } else if (role === ShigoRole.Publisher) {
      publisherPermissions.forEach((permission) => resolved.add(permission));
    } else if (role === ShigoRole.Advertiser) {
      advertiserPermissions.forEach((permission) => resolved.add(permission));
    } else if (role === ShigoRole.AdvertiserReadOnly) {
      advertiserReadPermissions.forEach((permission) => resolved.add(permission));
    }
  }
  return [...resolved];
}
